import React, { useState } from 'react'
import { StyleSheet } from 'react-native'
import sortBy from 'lodash/sortBy'
import { TrainingEvent } from '@alucio/aws-beacon-amplify/src/models'
import { DNABox, DNACard, DNADivider, DNAText, GridList } from '@alucio/lux-ui'
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors'
import { GridListSingleColumnHeader } from 'src/components/DNA'
import DNATrainingEventDefaultRow from 'src/components/DNA/FlatList/Rows/default/DNATrainingEventDefaultRow'
import { TenantORM } from 'src/state/redux/selector/tenant'
import { TrainingEventORM } from 'src/types/orms'

const styles = StyleSheet.create({
  emptyStateText: {
    color: colors['color-gray-300'],
  },
  header: {
    paddingVertical: 12,
  },
  headerWrapper: {
    paddingVertical: 12,
    marginLeft: 28,
  },
  wrapper: {
    minWidth: 1650,
  },
})

interface TrainingEventsListProps {
  trainingEvents: TrainingEventORM[]
  tenantORM: TenantORM
}

enum SortOrders {
  'ascending',
  'descending'
}

type SortOrder = keyof typeof SortOrders
type trainingEventFields = keyof TrainingEvent
interface SelectedSort {
  fieldName: trainingEventFields
  sortOrder: SortOrder
}

interface TrainigEventListConfig {
  fieldName: string,
  fieldLabel: string,
  fieldType: ('LABEL' | 'ACTION_MENU' ),
  width: number,
  unsortable?: boolean,
}

const fields: TrainigEventListConfig[] = [
  {
    fieldName: 'title',
    fieldLabel: 'Event Title',
    fieldType: 'LABEL',
    width: 6,
  },
  {
    fieldName: 'startDateTime',
    fieldLabel: 'Start Time',
    fieldType: 'LABEL',
    width: 4,
  },
  {
    fieldName: 'endDateTime',
    fieldLabel: 'End Time',
    fieldType: 'LABEL',
    width: 4,
  },
  {
    fieldName: 'status',
    fieldLabel: 'Status',
    fieldType: 'LABEL',
    width: 3,
    unsortable: true,
  },
]

const TrainingEventsList = (props: TrainingEventsListProps) => {
  const { trainingEvents, tenantORM } = props
  const [selectedSort, setSelectedSort] = useState<SelectedSort>(
    {
      fieldName: 'startDateTime',
      sortOrder: 'ascending',
    },
  )

  const sortedTrainingEvens = selectedSort.sortOrder === 'ascending'
    ? sortBy(trainingEvents, selectedSort.fieldName)
    : sortBy(trainingEvents, selectedSort.fieldName).reverse()

  if (!trainingEvents.length) {
    return (
      <DNABox alignX="center" alignY="center" fill>
        <DNAText style={styles.emptyStateText}>No events</DNAText>
      </DNABox>
    )
  }

  const toggleSort = (fieldName: keyof TrainingEvent) => {
    const fieldIsActive = selectedSort.fieldName = fieldName
    if (fieldIsActive) {
      setSelectedSort(p => ({ ...p, sortOrder:  selectedSort.sortOrder === 'ascending' ? 'descending' : 'ascending' }))
    } else {
      setSelectedSort({
        fieldName: fieldName,
        sortOrder: 'ascending',
      })
    }
  }

  const headerElements = fields.map(field => {
    const sortDir = selectedSort.fieldName === field.fieldName
      ? selectedSort.sortOrder === 'ascending'
        ? 'asc'
        : 'desc'
      : 'none';
    return (
      <GridList.Col style={[styles.headerWrapper]} span={field.width} key={field.fieldName}>
        {field.unsortable
          ? <DNAText status="tertiary" label>{field.fieldLabel}</DNAText>
          : <GridListSingleColumnHeader
              key={field.fieldLabel}
              field={field}
              style={styles.header}
              toggleSort={toggleSort}
              sortDir={sortDir}
          />
      }
      </GridList.Col>)
  })

  return (
    <DNABox>
      <DNACard style={styles.wrapper}>
        <GridList cols={'50px repeat(18, minmax(50px, 1fr))'} gap="10px">
          <GridList.Header style={{ backgroundColor: colors['color-gray-10'] }}>
            {headerElements}
          </GridList.Header>
          {/* CONTENT */}
          {
            sortedTrainingEvens.map((trainingEvent) => (
              <React.Fragment key={trainingEvent.model.id}>
                <DNADivider />
                <DNATrainingEventDefaultRow trainingEvent={trainingEvent} tenantORM={tenantORM} />
              </React.Fragment>
            ))
          }
        </GridList>
      </DNACard>
    </DNABox>
  );
}

export default TrainingEventsList
